import { useMutation, useQueryClient } from "@tanstack/react-query";
import { post } from "api/api-methods";
import { buildSearchParams } from "components/stepper/use-booking-navigate";
import { useError } from "hooks/use-error";
import { useNavigate } from "react-router-dom";
import { State } from "store/store-types";
import { CreateReservationParameters } from "types/request-parameters";
import { WebBooking } from "types/web-booking";
import { isGuid } from "utils";

export const useCreateBooking = () => {
    const { setErrorCode, openReservationFailedModal } = useError();
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const createReservation = async ({
        state,
        isInquiry,
    }: {
        state: State;
        isInquiry?: boolean;
    }) => {
        const data: CreateReservationParameters = new CreateReservationParameters(
            state,
            undefined,
            isInquiry
        );
        let booking: WebBooking;

        try {
            booking = await post<WebBooking, CreateReservationParameters>(
                `WebBooking/WebBookings`,
                data
            );
            const pathParts = window.location.pathname.split("/").filter((part) => part);

            // Check if the last segment is a GUID
            const lastPart = pathParts[pathParts.length - 1];
            const hasGuidAtEnd = isGuid(lastPart);

            // Replace or append the GUID
            if (hasGuidAtEnd) {
                pathParts[pathParts.length - 1] = booking.guid;
            } else {
                pathParts.push(booking.guid);
            }

            // Reconstruct the path
            const pathname = `/${pathParts.join("/")}`;

            const search = buildSearchParams(
                {
                    guests: null,
                    children: null,
                    date: null,
                },
                location.search
            );
            void navigate(
                {
                    pathname,
                    search,
                },
                { replace: true }
            );

            // update the React Query cache
            queryClient.setQueryData<WebBooking>(
                ["booking", booking.guid],
                new WebBooking(booking)
            );

            return booking;
        } catch (error) {
            const response = (error as { response?: { data?: { code: number } } })?.response?.data;
            const errorCode = response?.code;

            if (typeof errorCode === "number") {
                setErrorCode(errorCode);
                openReservationFailedModal();
            }

            throw error;
        }
    };

    return useMutation({
        mutationFn: createReservation,
        scope: {
            id: "webbooking-create",
        },
    });
};
